import { Injectable } from '@angular/core';
import { CollaboratorRole } from '@app/core/models';
import { PartnerFieldDefinition, PartnerRoleConfig } from '../models/partner.models';

/**
 * Static, config-driven source of truth for what each role's application
 * form asks beyond the shared basics (name, email, headline, bio, years of
 * experience, availability, resume, links — all strongly typed directly on
 * PartnerApplication, not here).
 *
 * Adding a new role — or a new field on an existing one — is a change to
 * the ROLE_CONFIGS array below only. Nothing that consumes this service
 * (apply form, role cards, admin review view) needs to change shape.
 *
 * Field design intent: every field here exists to produce *evidence*, not
 * just a self-rating — a GitHub link over a "rate your skill" slider, a
 * portfolio URL over a checkbox. That's what makes a resulting profile feel
 * earned rather than merely filled in.
 */
@Injectable({ providedIn: 'root' })
export class PartnerRoleConfigService {
  private readonly configs: PartnerRoleConfig[] = [
    {
      role: 'developer',
      label: 'Developer',
      tagline: 'Ship the code that AI can\u2019t finish alone.',
      description:
        'Pick up already-scoped tickets inside funded Angularize projects — ' +
        'the AI gets a build to ~70%, you take it the rest of the way. Fixed ' +
        'price per story point, no bidding, no cold outreach.',
      icon: 'code',
      fields: [
        {
          id: 'primaryStack',
          label: 'Primary tech stack',
          type: 'chips',
          required: true,
          placeholder: 'Angular, TypeScript, Node.js\u2026',
          helpText:
            'List the languages and frameworks you\u2019re strongest in — this is ' +
            'what we match you against, not a wishlist.',
          maxSelections: 12,
        },
        {
          id: 'githubUrl',
          label: 'GitHub profile',
          type: 'url',
          required: true,
          placeholder: 'https://github.com/yourhandle',
          helpText:
            'We look at real commit history and shipped repos — a live ' +
            'profile speaks louder than a description of one.',
        },
        {
          id: 'otherTools',
          label: 'Other tools & services',
          type: 'text',
          required: false,
          placeholder: 'Firebase, Docker, AWS, Stripe\u2026',
          helpText: 'Anything else you\u2019re comfortable working with.',
        },
      ],
    },
    {
      role: 'designer',
      label: 'UI/UX Designer',
      tagline: 'Turn a working build into a product people trust.',
      description:
        'Take AI-generated screens from functional to genuinely usable — ' +
        'flows, hierarchy, and polish that make a buyer\u2019s product feel real.',
      icon: 'palette',
      fields: [
        {
          id: 'portfolioUrl',
          label: 'Portfolio link',
          type: 'url',
          required: true,
          placeholder: 'https://your-portfolio.com',
          helpText:
            'Your strongest work, in one place — this is the first thing ' +
            'we\u2019ll look at.',
        },
        {
          id: 'figmaUrl',
          label: 'Figma profile (optional)',
          type: 'url',
          required: false,
          placeholder: 'https://figma.com/@yourhandle',
        },
        {
          id: 'designTools',
          label: 'Design tools',
          type: 'chips',
          required: true,
          placeholder: 'Figma, Framer, Adobe XD\u2026',
          maxSelections: 8,
        },
      ],
    },
    {
      role: 'graphic-designer',
      label: 'Graphic Designer',
      tagline: 'Give brands and visual assets their finished look.',
      description:
        'Logos, brand systems, marketing assets, and visual polish for ' +
        'projects where the identity matters as much as the build.',
      icon: 'brush',
      fields: [
        {
          id: 'portfolioUrl',
          label: 'Portfolio link',
          type: 'url',
          required: true,
          placeholder: 'https://your-portfolio.com',
          helpText: 'Behance, Dribbble, or a personal site — wherever your best work lives.',
        },
        {
          id: 'behanceOrDribbbleUrl',
          label: 'Behance / Dribbble (optional)',
          type: 'url',
          required: false,
          placeholder: 'https://dribbble.com/yourhandle',
        },
        {
          id: 'designTools',
          label: 'Tools',
          type: 'chips',
          required: true,
          placeholder: 'Photoshop, Illustrator, Procreate\u2026',
          maxSelections: 8,
        },
      ],
    },
    {
      role: 'qa',
      label: 'QA',
      tagline: 'Catch what shouldn\u2019t ship before the buyer does.',
      description:
        'Test AI-built features against real acceptance criteria — ' +
        'functional, edge-case, and regression coverage before work is ' +
        'marked done.',
      icon: 'bug_report',
      fields: [
        {
          id: 'testingTools',
          label: 'Testing tools & frameworks',
          type: 'chips',
          required: true,
          placeholder: 'Cypress, Selenium, Postman, Jira\u2026',
          maxSelections: 10,
        },
        {
          id: 'automationExperience',
          label: 'Test automation experience',
          type: 'select',
          required: true,
          options: [
            { label: 'None — manual testing only', value: 'none' },
            { label: 'Beginner', value: 'beginner' },
            { label: 'Intermediate', value: 'intermediate' },
            { label: 'Advanced', value: 'advanced' },
          ],
        },
        {
          id: 'certifications',
          label: 'Certifications (optional)',
          type: 'text',
          required: false,
          placeholder: 'ISTQB, CSTE\u2026',
        },
      ],
    },
    {
      role: 'pm',
      label: 'Product Manager',
      tagline: 'Keep a funded project moving from scope to delivery.',
      description:
        'Coordinate the team, track scope against acceptance criteria, and ' +
        'make sure what gets delivered is what the buyer actually asked for.',
      icon: 'assignment_turned_in',
      fields: [
        {
          id: 'pmTools',
          label: 'Tools you run projects in',
          type: 'chips',
          required: true,
          placeholder: 'Jira, Asana, Notion, Linear\u2026',
          maxSelections: 8,
        },
        {
          id: 'industriesWorked',
          label: 'Industries you\u2019ve worked in (optional)',
          type: 'chips',
          required: false,
          placeholder: 'Fintech, e-commerce, healthcare\u2026',
          maxSelections: 8,
        },
        {
          id: 'certifications',
          label: 'Certifications (optional)',
          type: 'text',
          required: false,
          placeholder: 'PMP, CSM\u2026',
        },
      ],
    },
  ];

  /** All roles currently open for application — drives the landing page's
   *  role cards, in the order they should display. */
  listRoles(): PartnerRoleConfig[] {
    return this.configs;
  }

  /** One role's full config, or undefined if the role isn't open for
   *  application (shouldn't happen given CollaboratorRole is closed, but
   *  callers should still handle it rather than assume). */
  getRoleConfig(role: CollaboratorRole): PartnerRoleConfig | undefined {
    return this.configs.find((c) => c.role === role);
  }

  /** Just this role's additional fields — what the apply form renders
   *  after its hardcoded shared-field section. Empty array (not undefined)
   *  if the role isn't found, so callers can iterate safely either way. */
  getFields(role: CollaboratorRole): PartnerFieldDefinition[] {
    return this.getRoleConfig(role)?.fields ?? [];
  }
}